import { CommonModule } from '@angular/common';
import {
	Component,
	inject,
	PLATFORM_ID,
	Renderer2,
	signal,
	ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';

import { TranslatePipe } from '@ngx-translate/core';
import { MarkdownModule } from 'ngx-markdown';
import type { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs';

import { GoogleMapComponent } from '@valk-nx/components/ui-google-map/src/lib/google-map';
import { IconListComponent } from '@valk-nx/components/ui-icon-list/src/lib/icon-list.component';
import type { Icon } from '@valk-nx/components/ui-icon-list/src/lib/icon-list.interface';
import type { ImageInterface } from '@valk-nx/components/ui-image/src/lib/image.interface';
import { ListGridComponent } from '@valk-nx/components/ui-list-grid/src/lib/list-grid';
import { MiniCarouselComponent } from '@valk-nx/components/ui-mini-carousel/src/lib/mini-carousel';
import { PricingComponent } from '@valk-nx/components/ui-pricing/src/lib/pricing';
import { RatingComponent } from '@valk-nx/components/ui-rating/src/lib/rating';
import { ReadMoreComponent } from '@valk-nx/components/ui-read-more/src/lib/read-more';
import { TabMenuComponent } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu';
import { TabMenuItemDirective } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu.directive';
import { TagComponent } from '@valk-nx/components/ui-tag/src/lib/tag';
import { ImageGridComponent } from '@valk-nx/compositions/ui-image-grid/src/lib/image-grid';
import type { Language } from '@valk-nx/core/lib/core';
import { fallbackLanguage, HOTEL_SLUG } from '@valk-nx/core/lib/core';
import { PriceHelper } from '@valk-nx/helpers/lib/price/price.helper';
import type { PricingTooltipOccupancy } from '@valk-nx/helpers/lib/pricing-tooltip/pricing-tooltip.interface';
import { AvailabilityWidgetComponent } from '@valk-nx/storyblok/components/availability-widget/src/lib/availability-widget';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { buildIconListRoom } from '@valk-nx/storyblok-helpers/src/lib/general/icon-list.helper';
import { ContentService } from '@valk-nx/storyblok-services/src/lib/services/content.service';
import { MetadataService } from '@valk-nx/storyblok-services/src/lib/services/metadata.service';
import { StoryblokService } from '@valk-nx/storyblok-services/src/lib/services/storyblok.service';
import { HotelStore } from '@valk-nx/storyblok-store/src/lib/store/hotel/hotel.store';
import type { RoomData } from '@valk-nx/storyblok-types/src/lib/types/content.interface';

@Component({
	encapsulation: ViewEncapsulation.None,
	selector: 'sb-room-detail',
	templateUrl: './room-detail.component.html',
	imports: [
		AvailabilityWidgetComponent,
		CommonModule,
		IconListComponent,
		ImageGridComponent,
		ListGridComponent,
		MarkdownModule,
		GoogleMapComponent,
		PricingComponent,
		ReadMoreComponent,
		MiniCarouselComponent,
		RatingComponent,
		RouterModule,
		TabMenuComponent,
		TabMenuItemDirective,
		TagComponent,
		TranslatePipe,
	],
})
export class RoomDetailComponent extends StoryblokRootDirective {
	private readonly sbService = inject(StoryblokService);
	private readonly contentService = inject(ContentService);
	private readonly metadataService = inject(MetadataService);
	private readonly renderer2 = inject(Renderer2);
	readonly platformId = inject(PLATFORM_ID);
	readonly hotelSlug = inject(HOTEL_SLUG);

	readonly currentLanguage = signal<Language>(fallbackLanguage);

	readonly hotelStore = inject(HotelStore);

	roomSlug = '';
	isSmallTablet$: Observable<boolean>;
	roomData$: Observable<
		RoomData & {
			displayPrice: number;
			hasImages: boolean;
			priceConditionKey: string;
			pricingToolTipOccupancy: PricingTooltipOccupancy;
		}
	>;
	hotelImages: ImageInterface[] = [];
	roomFacilityIconList: Icon[] = [];
	roomFacilities: string[] = [];
	disabledReadmore = true;

	constructor() {
		super();

		this.currentLanguage.set(
			(this.translate.getCurrentLang() ??
				this.translate.getFallbackLang()) as Language,
		);

		this.roomData$ = this.sbService.translatedSlugsForCurrentStory().pipe(
			takeUntilDestroyed(),
			map((translatedSlugs) => {
				return (
					translatedSlugs.find((x) => x.selected) ??
					translatedSlugs[0]
				);
			}),
			switchMap((slug) => {
				this.roomSlug = slug.path.split('/').pop();
				return this.contentService.getRoom(
					this.roomSlug,
					slug.lang.toLowerCase(),
				);
			}),
			map((response) => {
				const { price } = response.data;
				const roomData = {
					...response.data,
					displayPrice:
						price.type === 'perPerson' || price.type === 'perNight'
							? (price.displayAmount ?? 0)
							: price.amount,
					hasImages: response.data.images.length > 0,
					priceConditionKey: this.priceConditionKey(price.type),
					pricingToolTipOccupancy: PriceHelper.getPricingTooltip(
						response.data.occupancyRestrictions,
					),
				};
				this.metadataService.updateMetaDataBatch(
					roomData,
					this.robotsNoFollow(),
					this.robotsNoIndex(),
				);
				this.metadataService.setSchemaOrgRoomDetail(
					this.renderer2,
					roomData,
					this.hotelStore.hotelMainContact(),
				);

				this.roomFacilityIconList = buildIconListRoom(roomData);

				const {
					roomConvenience,
					accessibility,
					sleepComfort,
					bathroom,
				} = roomData.facilities;
				const optionalAccessibility = accessibility ?? [];
				const facilities = [
					...sleepComfort,
					...bathroom,
					...roomConvenience,
					...optionalAccessibility,
				];
				this.roomFacilities = [...new Set(facilities)];

				this.hotelImages = roomData.hotel?.images.map((image) => {
					return {
						src: image.src,
						altText: image.alt,
						width: 0,
						height: 0,
					};
				});

				return roomData;
			}),
		);
	}

	private priceConditionKey(type: RoomData['price']['type']) {
		switch (type) {
			case 'perPerson':
				return 'global.price-per-person';
			case 'perNight':
				return 'global.price-per-night';
			default:
				return 'global.price';
		}
	}
}
